// @ts-check
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react"
import { useSession } from "../../../../../hooks/auth/useSession.hook"
import useCtrlClickTableTip from "../../../../../hooks/aikaTips/useCtrlClickTableTip.hook"
import { Table } from "../../../../../components/table/Table.component"
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import useisHoldingModifierDown from "../../../../../hooks/useIsHoldingModifierDown"
import { buildClientTasksReportTableColumns } from "./ClientTasksReportTable.columns"
import { useClientTasksReportTableFilters } from "./ClientTasksReportTable.filters"

import { EmptyTableMessage } from "../../../../../components/compounds/customMessages/EmptyTableMessage.compound"
import { useTaskReportsSorting } from "../../../../../hooks/reports/useTaskReportsSorting.hook"
import { useTaskReportsFilters } from "../../../../../hooks/reports/useTaskReportsFilters.hook"

/**
 * @type {React.ForwardRefExoticComponent<
 *   import("./ClientTasksReportTable.controller.types").ClientTasksReportTableProps &
 *   React.RefAttributes<import("./ClientTasksReportTable.controller.types").ClientTasksReportTableControllerRef>
 * >}
 */
const ClientColumnDataTable = forwardRef((props, ref) => {
  const {
    value,
    totals,
    contextMenuRef,
    setContextMenuTarget,
    updatingTask,
    categories,
    modules,
    projects,
    teamMembers,
    loading,
    infiniteScroll,
    onFilterValuesChange,
  } = props

  const { user } = useSession()
  const userIsAdmin = user?.isAdmin()

  const { sorting, handleSortingChange, resetSorting, getSortingValue } =
    useTaskReportsSorting("date")

  const { columnFilters, setColumnFilters, getFilterValues, setFilter } =
    useTaskReportsFilters({
      projectName: "projectCode",
      teamMemberNameTag: "teamMemberNameTag",
      moduleId: "module",
      taskName: "task",
    })

  const isHoldingModifierDown = useisHoldingModifierDown()
  const { isTipActive } = useCtrlClickTableTip()
  const {
    teamMemberFilterTemplate,
    categoryFilterTemplate,
    moduleFilterTemplate,
    clientFilterTemplate,
    taskNameFilterTemplate,
  } = useClientTasksReportTableFilters({
    teamMembers,
    categories,
    modules,
    projects,
    loading,
  })

  const columns = useMemo(
    () =>
      buildClientTasksReportTableColumns({
        totals,
        isHoldingModifierDown,
        contextMenuRef,
        setContextMenuTarget,
        userIsAdmin,
        updatingTask,
        isTipActive,
        loading,
        teamMemberFilterTemplate,
        categoryFilterTemplate,
        moduleFilterTemplate,
        clientFilterTemplate,
        taskNameFilterTemplate,
      }),
    [
      totals,
      isHoldingModifierDown,
      contextMenuRef,
      setContextMenuTarget,
      userIsAdmin,
      updatingTask,
      isTipActive,
      loading,
      teamMemberFilterTemplate,
      categoryFilterTemplate,
      moduleFilterTemplate,
      clientFilterTemplate,
      taskNameFilterTemplate,
    ]
  )

  const table = useReactTable({
    data: value,
    columns,
    enableSortingRemoval: true,
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    groupedColumnMode: "remove",
    isMultiSortEvent: () => true,
    maxMultiSortColCount: 2,
    initialState: {
      columnPinning: {
        left: ["projectCode"],
      },
      grouping: ["date"],
      expanded: true,
    },
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    manualFiltering: true,
  })

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        resetSorting()
        table.resetColumnFilters(true)
      },
      getFilterValues: () => getFilterValues(table),
      getSortingValue: () => getSortingValue(table),
      setFilter: (/** @type {string} */ field, /** @type {unknown} */ value) =>
        setFilter(table, field, value),
    }),
    [table]
  )

  useEffect(() => {
    if (!onFilterValuesChange) {
      return
    }

    onFilterValuesChange(getFilterValues(table))
  }, [columnFilters, onFilterValuesChange, table])

  /**
   * @param {React.MouseEvent} e
   * @param {import("./ClientTasksReportTable.controller.types").ClientTasksReportTableRow} row
   */
  function handleContextMenu(e, row) {
    if (!userIsAdmin || !row.original || row.getIsGrouped()) {
      return
    }

    setContextMenuTarget(row.original)
    contextMenuRef?.current?.show(e)
  }

  return (
    <Table
      table={table}
      estimatedRowHeight={() => 37}
      isLoading={loading}
      emptyMessage={<EmptyTableMessage />}
      className="client-tasks__table"
      enableStickyGroupHeaders
      getIsRowSticky={(row) => row.getIsGrouped()}
      onContextMenu={handleContextMenu}
      infiniteScroll={infiniteScroll}
    />
  )
})

// Devtools display name
ClientColumnDataTable.displayName = "ClientColumnDataTable"
export default ClientColumnDataTable
